/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import isNil from 'lodash/isNil';
import {
    DisplayHtmlContent,
    SlideShowWithFetch,
    FlexCol,
    FlexRow,
    Heading,
    MobileCoverageRating,
    StarRating,
    useWindowSize,
} from 'sarsaparilla';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { FavoriteHeart } from 'shared-ui/favorites';
import HeaderPlaceholder from './HeaderPlaceholder';
import type { TreePermitData, RatingData, ParentLocation } from '../../../types/permit';

interface HeaderType {
    treePermit: TreePermitData;
    rating: RatingData;
    parentLocation: Array<ParentLocation>;
    isLoading: boolean;
}

export default function Header({
    treePermit,
    rating,
    parentLocation = [],
    isLoading,
}: HeaderType) {
    const { width } = useWindowSize();
    const isMobile = width && width <= 768;
    const location = [];
    const metadata = [];
    const parentLocationSelected = parentLocation?.find(
        (item) => item.entity_id === treePermit?.parent_asset_id
    );

    if (!isNil(parentLocationSelected?.name)) {
        location.push(
            <span>
                <a href={getGatewayUrl(treePermit.parent_asset_id)}>
                    {parentLocationSelected?.name}
                </a>
            </span>
        );
    }

    if (!isNil(parentLocationSelected?.state_code)) {
        location.push(parentLocationSelected?.state_code);
    }

    if (!isNil(rating?.average_rating) && !isNil(rating?.number_of_ratings)) {
        metadata.push(
            <StarRating
                rating={rating?.average_rating}
                reviewCount={rating?.number_of_ratings}
            />
        );
    }

    if (rating?.mobile_coverage_rating) {
        metadata.push(<MobileCoverageRating rating={rating?.mobile_coverage_rating} />);
    }

    metadata.push(
        <FavoriteHeart
            entityId={treePermit?.asset_id}
            entityType={'treepermit'}
            showFavoriteText
            key="favorite-button"
        />
    );

    const hasLocation = !!location.length;
    const hasMetadata = !!metadata.length;

    if (isLoading) {
        return <HeaderPlaceholder />;
    }

    return (
        <div className="tp-header">
            {isMobile && (
                <FlexRow>
                    <FlexCol lg={12}>
                        <SlideShowWithFetch assetId={treePermit?.asset_id} />
                    </FlexCol>
                </FlexRow>
            )}
            <FlexRow>
                <FlexCol lg={6} className="mb-3 mb-lg-0">
                    <Heading
                        headingLevel={2}
                        appearance="h5Caps"
                        className="tp-header-subtitle"
                    >
                        Seasonal Tree Permit
                    </Heading>
                    <Heading headingLevel={1} appearance="h4" className="tp-header-title">
                        {treePermit?.name}
                    </Heading>
                    {hasLocation && (
                        <ul className="tp-header-metadata-list">
                            {location.map((item, key) => {
                                return (
                                    <li
                                        className="tp-header-metadata-list-item"
                                        key={key}
                                    >
                                        {item}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {hasMetadata && (
                        <ul className="tp-header-metadata-list">
                            {metadata.map((item, key) => {
                                return (
                                    <li
                                        className="tp-header-metadata-list-item"
                                        key={key}
                                    >
                                        {item}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {!isNil(treePermit?.overview?.paragraphs) && (
                        <div className="tp-header-description">
                            {treePermit?.overview?.paragraphs.map((item, index) => (
                                <DisplayHtmlContent key={index} html={item} />
                            ))}
                        </div>
                    )}
                </FlexCol>
                {!isMobile && (
                    <FlexCol lg={6}>
                        <SlideShowWithFetch assetId={treePermit?.asset_id} />
                    </FlexCol>
                )}
            </FlexRow>
        </div>
    );
}
